import { useState } from 'react';
import { GlassModal } from '@/components/ui/glass-modal';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';
import { Loader2, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';

interface BulkPriceEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filteredCount: number;
  pricedItemsCount: number;
  onConfirm: (mode: 'percentage' | 'fixed', value: number) => Promise<void>;
  isProcessing: boolean;
}

export function BulkPriceEditDialog({
  open,
  onOpenChange,
  filteredCount,
  pricedItemsCount,
  onConfirm,
  isProcessing,
}: BulkPriceEditDialogProps) {
  const [mode, setMode] = useState<'percentage' | 'fixed'>('percentage');
  const [percentValue, setPercentValue] = useState('10');
  const [isIncrease, setIsIncrease] = useState(true);
  const [fixedValue, setFixedValue] = useState('');

  const handleConfirm = async () => {
    if (mode === 'percentage') {
      const percent = parseFloat(percentValue) || 0;
      const adjustedPercent = isIncrease ? percent : -percent;
      await onConfirm('percentage', adjustedPercent);
    } else {
      const fixed = parseFloat(fixedValue) || 0;
      await onConfirm('fixed', fixed);
    }
  };

  const isValid = mode === 'percentage'
    ? parseFloat(percentValue) > 0 && parseFloat(percentValue) <= 999
    : parseFloat(fixedValue) > 0;

  return (
    <GlassModal
      open={open}
      onOpenChange={onOpenChange}
      title="Adjust Prices"
      description={`Update prices for ${pricedItemsCount} items with existing prices.`}
    >
      <div className="mb-2">
        <h2 className="text-base font-semibold">Adjust Prices</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Update prices for {pricedItemsCount} item{pricedItemsCount !== 1 ? 's' : ''} with existing prices.
          {pricedItemsCount < filteredCount && (
            <span className="block mt-1">
              ({filteredCount - pricedItemsCount} item{filteredCount - pricedItemsCount !== 1 ? 's' : ''} without prices will be skipped)
            </span>
          )}
        </p>
      </div>

      <div className="space-y-6 py-4">
        <RadioGroup
          value={mode}
          onValueChange={(v) => setMode(v as 'percentage' | 'fixed')}
          className="grid grid-cols-2 gap-3"
        >
          <Label
            htmlFor="mode-percentage"
            className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
              mode === 'percentage'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-white/20'
            }`}
          >
            <RadioGroupItem value="percentage" id="mode-percentage" />
            <span>By Percentage</span>
          </Label>
          <Label
            htmlFor="mode-fixed"
            className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
              mode === 'fixed'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-white/20'
            }`}
          >
            <RadioGroupItem value="fixed" id="mode-fixed" />
            <span>Fixed Amount</span>
          </Label>
        </RadioGroup>

        {mode === 'percentage' && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIsIncrease(true)}
                className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-md border transition-colors ${
                  isIncrease
                    ? 'border-success/50 bg-success/10 text-success'
                    : 'border-border hover:border-white/20'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                Increase
              </button>
              <button
                type="button"
                onClick={() => setIsIncrease(false)}
                className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-md border transition-colors ${
                  !isIncrease
                    ? 'border-destructive/50 bg-destructive/10 text-destructive'
                    : 'border-border hover:border-white/20'
                }`}
              >
                <TrendingDown className="w-4 h-4" />
                Decrease
              </button>
            </div>
            <div className="relative">
              <Input
                type="number"
                min="0.1"
                max="999"
                step="0.1"
                value={percentValue}
                onChange={(e) => setPercentValue(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                className="pr-8"
                placeholder="10"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                %
              </span>
            </div>
          </div>
        )}

        {mode === 'fixed' && (
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              inputMode="decimal"
              pattern="[0-9]*\.?[0-9]*"
              {...PRICE_INPUT_PROPS}
              value={fixedValue}
              onChange={(e) => setFixedValue(e.target.value.replace(/[^0-9.]/g, ''))}
              className="pl-8"
              placeholder="0.00"
            />
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isProcessing}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isProcessing || !isValid || pricedItemsCount === 0}
          className="flex-1"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Updating...
            </>
          ) : (
            'Update Prices'
          )}
        </Button>
      </div>
    </GlassModal>
  );
}
